/**
 *
 *
 *
 *
 */
import { heureka } from "@otto-ec/heureka";
import { cookie } from "@otto-ec/global-resources/cookie";

export interface SizePreferences {
  shoeSizeEUV2: string[];
  shoeSizeUKV2: string[];
  shoeSizeUSV2?: string[];
}

export class SizePreferencesCookieStore {
  private static readonly COOKIE_NAME = "sizePreferences";
  private static readonly TTL_DAYS = 30;
  private static lastCookieState: SizePreferences | null = null; /*                              */

  /**
 *
 *
 */
  static get(): SizePreferences | null {
    if (typeof document === "undefined" || !this.exists()) {
      return null;
    }

    const sizeCookie = cookie.get(this.COOKIE_NAME);
    if (!sizeCookie) {
      /*                                                      */
      return null;
    }

    try {
      const decodedValue = atob(sizeCookie);
      return JSON.parse(decodedValue) as SizePreferences;
    } catch (error) {
      void error; /*               */
      return null;
    }
  }

  /**
 *
 *
 */
  private static set(preferences: SizePreferences): void {
    if (typeof document === "undefined" || !cookie) {
      return;
    }

    try {
      cookie.set(this.COOKIE_NAME, btoa(JSON.stringify(preferences)), { days: 30, path: "/", samesite: "Strict" });
    } catch (error) {
      void error; /*               */
    }
  }

  /**
 *
 *
 *
 */
  static add(sizeSystem: string, sizes: string[]): void {
    if (typeof document === "undefined") {
      return;
    }

    let current = this.get();
    if (!current) {
      current = this.emptyPreferences();
    }

    /*                                           */
    const sizeSystemKey = this.sizeSystemKeyFromString(sizeSystem);
    const currentSizes = current[sizeSystemKey] || [];

    /*                                          */
    const newSizes = sizes.filter((size) => !currentSizes.includes(size));
    current[sizeSystemKey] = [...currentSizes, ...newSizes];

    this.set(current);
    heureka.filterPersonalization_saved.emit([]);
  }

  /**
 *
 *
 *
 */
  static delete(sizeSystem: string, sizes: string[]): void {
    if (typeof document === "undefined" || !this.exists()) {
      return;
    }

    const current = this.get();
    if (!current) {
      return;
    }

    /*                                          */
    const sizeSystemKey = this.sizeSystemKeyFromString(sizeSystem);
    const currentSizes = current[sizeSystemKey];
    if (currentSizes) {
      current[sizeSystemKey] = currentSizes.filter((size) => !sizes.includes(size));
    }

    this.set(current);
    heureka.filterPersonalization_deleted.emit([]);
  }

  /**
 *
 */
  static deleteAll(): void {
    if (typeof document === "undefined" || !this.exists()) {
      return;
    }

    this.lastCookieState = this.get();
    this.set(this.emptyPreferences());
    heureka.filterPersonalization_deleted.emit([]);
  }

  /**
 *
 */
  static restoreCookie(): void {
    if (typeof document === "undefined" || !this.lastCookieState) {
      return;
    }
    this.set(this.lastCookieState);
    heureka.filterPersonalization_saved.emit([]);
  }

  /**
 *
 *
 */
  static exists(): boolean {
    if (typeof document === "undefined") {
      return false;
    }
    return !!cookie.get(this.COOKIE_NAME);
  }

  static toString(): string {
    const preferences = this.get();
    if (!preferences) {
      return "";
    }

    const sizeConfigs = [
      { label: "EU", sizes: preferences.shoeSizeEUV2 },
      { label: "UK", sizes: preferences.shoeSizeUKV2 },
      { label: "US", sizes: preferences.shoeSizeUSV2 || [] },
    ];

    return sizeConfigs
      .filter(({ sizes }) => sizes.length > 0)
      .map(({ label, sizes }) => `${label} ${sizes.join(`|${label} `)}`)
      .join("|");
  }

  /**
 *
 *
 *
 */
  static isSizeSaved(title: string): boolean {
    if (!title) {
      return false;
    }

    const preferences = this.get();
    if (!preferences) {
      return false;
    }

    const sizeConfigs = [
      { label: "EU", sizes: preferences.shoeSizeEUV2 },
      { label: "UK", sizes: preferences.shoeSizeUKV2 },
      { label: "US", sizes: preferences.shoeSizeUSV2 },
    ];

    return sizeConfigs.some(({ label, sizes }) => sizes?.some((size) => `${label} ${size}` === title));
  }

  static hasAnyValues(): boolean {
    const preferences = this.get();
    if (!preferences) {
      return false;
    }

    return (
      (preferences.shoeSizeEUV2?.length ?? 0) > 0 ||
      (preferences.shoeSizeUKV2?.length ?? 0) > 0 ||
      (preferences.shoeSizeUSV2?.length ?? 0) > 0
    );
  }

  static sizeSystemKeyFromString(sizeSystem: string) {
    return `shoeSize${sizeSystem}V2` as keyof SizePreferences;
  }

  static emptyPreferences() {
    return { shoeSizeEUV2: [], shoeSizeUKV2: [], shoeSizeUSV2: [] };
  }
}
